/**
 * URL parameter extraction for initial filter state and URL synchronization
 * 
 * Categories are stored directly in filter_metadata (IndexedDB) with structure:
 * { uniqueValues: {L: ['EN', 'RU', 'FR', ...], F: ['AUDIO', 'TEXT', 'SCAN', ...], M: [...], S: [...]} }
 * 
 * URL format: cat=FR-Tx-Bu where values are direct category codes from file properties.
 * They are matched against uniqueValues to determine which group (L/F/M/S) they belong to.
 */

// Get debug mode from URL (global for this module)
const urlParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
const debugMode = urlParams.get('debug') === '1' ? '1' : '';

/**
 * Extract URL parameters for initial filter state
 * Categories are matched against filter_metadata.uniqueValues to determine groups
 * Note: filter_metadata may not be available during initial extractUrlParameters call,
 * so categories are restored later when refreshFiltersFromMetadata is called
 */
export function extractUrlParameters(ctx, config) {
	try {
		const query = $.urlParam('sq');
		if (query) { 
			config.searchQuery = query; 
			ctx.activeFilters.searchQuery = query;
			// Set search query in UI input if available
			try {
				const $searchInput = window.jQuery ? window.jQuery('#fnd_searchbox, input[name="sq"], input[name="search"]').first() : null;
				if ($searchInput && $searchInput.length) { $searchInput.val(query); }
			} catch(_) {}
		}
		
		const categories = $.urlParam('cat');
		
		// Get filter metadata from global store or use ctx.filters if available
		// filter_metadata is loaded from IndexedDB and contains uniqueValues: {L: [], F: [], M: [], S: []}
		const filterMeta = window.__filterMetadata || 
			(ctx.filters && Object.keys(ctx.filters).length > 0 && ctx.filters.L ? 
				{ uniqueValues: { L: ctx.filters.L || [], F: ctx.filters.F || [], M: ctx.filters.M || [], S: ctx.filters.S || [] } } : 
				null);
		
		if (categories && filterMeta && filterMeta.uniqueValues) {
			// Parse dash-separated values like "FR-Tx-Bu"
			// These are direct category values from file properties (L, F, M, S fields)
			const values = String(categories).split('-');
			const uniqueValues = filterMeta.uniqueValues;
			
			
			for (const val of values) {
				const trimmed = val.trim();
				if (!trimmed) {
					continue;
				}
				
				// Match value against uniqueValues to determine group (L, F, M, S)
				let matched = false;
				for (const [group, groupValues] of Object.entries(uniqueValues)) {
					if (Array.isArray(groupValues) && groupValues.includes(trimmed)) {
						if (!config.startCategories[group]) {
							config.startCategories[group] = [];
						}
						
						// Add category to group if not already present
						if ($.inArray(trimmed, config.startCategories[group]) == -1) {
							config.startCategories[group].push(trimmed);
							matched = true;
							break;
						}
					}
				}
				
			}
			
		} else {
			if (false) {
				console.log('[FilterController] No categories param or filter metadata not available', { 
					categories: !!categories, 
					hasFilterMeta: !!filterMeta,
					hasUniqueValues: !!(filterMeta && filterMeta.uniqueValues)
				});
			}
		}
		// URL categories parsed logging only in debug mode
		
		// Extract date filters (defensive typeof: $.urlParam historically returned []
		// for absent params — an array here poisoned the filter badge count)
		const dateFrom = $.urlParam('dateFrom');
		if (dateFrom && typeof dateFrom === 'string') {
			ctx.activeFilters.dateFrom = dateFrom;
			// Set date in UI inputs if available
			try {
				const $dateFrom = window.jQuery ? window.jQuery('.filter-date-from') : null;
				if ($dateFrom && $dateFrom.length) { 
					$dateFrom.val(dateFrom);
				}
			} catch(_) {}
		}
		
		const dateTo = $.urlParam('dateTo');
		if (dateTo && typeof dateTo === 'string') {
			ctx.activeFilters.dateTo = dateTo;
			// Set date in UI inputs if available
			try {
				const $dateTo = window.jQuery ? window.jQuery('.filter-date-to') : null;
				if ($dateTo && $dateTo.length) { 
					$dateTo.val(dateTo);
				}
			} catch(_) {}
		}
		
		// URL parameters extraction logging only in debug mode
	} catch (error) {
		if (debugMode === '1') { console.error('[FilterController] Error extracting URL parameters:', error); }
	}
}

// Debounce timer for URL updates (to avoid too many history entries)
let urlUpdateTimeout = null;
let urlUpdateCallback = null; // Store callback function for flush
const URL_UPDATE_DEBOUNCE_MS = 1500; // 1.5 seconds debounce for all URL updates (search query and filters)

// Cancel pending URL update when user navigates back/forward
// This prevents creating duplicate history entries when user navigates before debounce completes
if (typeof window !== 'undefined') {
	window.addEventListener('popstate', () => {
		if (urlUpdateTimeout) {
			const debugMode = new URLSearchParams(window.location.search).get('debug') === '1';
			if (debugMode) {
				console.log('[FilterController] ⏹️ Cancelling pending URL update due to browser navigation');
			}
			clearTimeout(urlUpdateTimeout);
			urlUpdateTimeout = null;
			isUpdatingUrl = false;
		}
	});
}

// Track last URL state to avoid duplicate history entries
let lastUrlState = null;

// Export for browser history handler to reset state after restoration
if (typeof window !== 'undefined') {
    window.__filterControllerUrlState = { 
        lastUrlState,
        setLastUrlState: (state) => { lastUrlState = state; }
    };
}

/**
 * Serialize current filter state to URL string for comparison
 */
function serializeFilterState(ctx) {
	try {
		const filters = ctx.getActiveFilters();
		const selectedCategories = ctx.getSelectedCategories();
		return serializeFilterStateFromData(filters, selectedCategories);
	} catch (error) {
		return '';
	}
}

/**
 * Serialize filter state from already fetched data (to avoid multiple getSelectedCategories calls)
 */
function serializeFilterStateFromData(filters, selectedCategories) {
	try {
		const parts = [];
		
		// Search query
		if (filters.searchQuery && typeof filters.searchQuery === 'string' && filters.searchQuery.trim()) {
			parts.push(`sq=${encodeURIComponent(filters.searchQuery.trim())}`);
		}
		
		// Date filters
		if (filters.dateFrom && typeof filters.dateFrom === 'string' && filters.dateFrom.trim()) {
			parts.push(`dateFrom=${encodeURIComponent(filters.dateFrom.trim())}`);
		}
		if (filters.dateTo && typeof filters.dateTo === 'string' && filters.dateTo.trim()) {
			parts.push(`dateTo=${encodeURIComponent(filters.dateTo.trim())}`);
		}
		
		// Category filters
		if (selectedCategories && Object.keys(selectedCategories).length > 0) {
			const allValues = [];
			for (const [group, values] of Object.entries(selectedCategories)) {
				if (Array.isArray(values) && values.length > 0) {
					allValues.push(...values);
				}
			}
			if (allValues.length > 0) {
				parts.push(`cat=${allValues.join('-')}`);
			}
		}
		
		// Public only filter (pu parameter)
		const isAuthenticated = !!(window.SERVER_AUTH_STATUS && window.SERVER_AUTH_STATUS.isAuthenticated);
		if (!isAuthenticated) {
			const publicOnlyCheckbox = document.getElementById('public-only-filter');
			if (publicOnlyCheckbox) {
				if (publicOnlyCheckbox.checked) {
					parts.push('pu=1');
				} else {
					parts.push('pu=0');
				}
			}
		}
		
		return parts.sort().join('&'); // Sort for consistent comparison
	} catch (error) {
		return '';
	}
}

/**
 * Update URL with current filter state (without page reload)
 * Creates history entries for browser back/forward navigation
 * @param {Object} ctx - FilterController context
 * @param {boolean} immediate - If true, update immediately without debounce (for significant changes like search execution)
 * @param {string} desiredHash - Optional desired hash to set (e.g., '#fulltext'). If not provided, current hash is preserved.
 */
// Flag to prevent multiple simultaneous calls
let isUpdatingUrl = false;

export function updateUrlWithFilters(ctx, immediate = false, desiredHash = null) {
	try {
		if (debugMode === '1') {
			console.log('[FilterController] 🔄 updateUrlWithFilters called', { immediate, isUpdatingUrl, isRestoring: window.__isRestoringBrowserState });
		}
		
		// Check if we're restoring state from browser history (prevents creating duplicate history entries)
		const isRestoring = window.__isRestoringBrowserState === true;
		
		// Skip URL update if we're restoring state from browser history
		if (isRestoring) {
			if (debugMode === '1') {
				console.log('[FilterController] ⏭️ Skipping URL update - restoring from browser history');
			}
			return;
		}
		
		// Prevent multiple simultaneous calls
		if (isUpdatingUrl) {
			if (debugMode === '1') {
				console.log('[FilterController] ⏭️ URL update already in progress, skipping duplicate call');
			}
			return;
		}
		isUpdatingUrl = true;
		
		// CRITICAL: Save current hash at the moment of call, not when debounce fires
		// This ensures that hash is preserved correctly when user navigates before debounce completes
		// If desiredHash is provided, use it instead of current hash
		const savedHash = desiredHash !== null ? desiredHash : window.location.hash;
		
		if (debugMode === '1') {
			if (false) console.log('[FilterController] 🔄 updateUrlWithFilters called', {
				immediate,
				currentUrl: window.location.href,
				savedHash,
				lastUrlState,
				callStack: new Error().stack.split('\n').slice(1, 4).map(s => s.trim())
			});
		}
		
		// Track whether this is a debounced update (should use replaceState) or immediate (should use pushState)
		const updateUrl = (usePushState = true) => {
			try {
				// Get filters and categories once to avoid multiple calls
				const filters = ctx.getActiveFilters();
				const selectedCategories = ctx.getSelectedCategories();
				
				// Serialize current state for comparison (reuse already fetched data)
				const currentState = serializeFilterStateFromData(filters, selectedCategories);
				
				// Check if filters actually changed (any filter: sq, cat, dateFrom, dateTo, pu)
				// If filters changed → this is a NEW search → remove page parameter (reset to page 1)
				// If filters didn't change → this is PAGINATION → preserve page parameter
				const stateChangedFromLast = lastUrlState !== null && lastUrlState !== currentState;
				
				// CRITICAL: Determine which hash to use
				// Priority: desiredHash (if provided) > current hash (if it matches desired) > savedHash
				const currentHash = window.location.hash;
				let hashToUse;
				if (desiredHash !== null) {
					// desiredHash was provided - use it
					hashToUse = desiredHash;
				} else {
					// No desiredHash - preserve current hash (don't change it)
					hashToUse = currentHash || savedHash;
				}
				
				if (debugMode === '1') {
					if (false) console.log('[FilterController] 📊 State comparison:', {
						lastUrlState,
						currentState,
						stateChangedFromLast,
						willRemovePage: stateChangedFromLast,
						savedHash,
						desiredHash,
						currentHash,
						hashToUse,
						filters: {
							searchQuery: filters.searchQuery,
							dateFrom: filters.dateFrom,
							dateTo: filters.dateTo,
							categories: Object.keys(selectedCategories || {})
						}
					});
				}
				
				// Build URL to compare with current URL
				const url = new URL(window.location.href);
				
				// Use determined hash
				url.hash = hashToUse;
				
				// Get current page parameter from URL (before we modify URL)
				const currentPage = url.searchParams.get('page');
				
				// Update search query
		if (filters.searchQuery && typeof filters.searchQuery === 'string' && filters.searchQuery.trim()) {
					url.searchParams.set('sq', filters.searchQuery.trim());
				} else {
					url.searchParams.delete('sq');
				}
				
				// Update date filters
		if (filters.dateFrom && typeof filters.dateFrom === 'string' && filters.dateFrom.trim()) {
					url.searchParams.set('dateFrom', filters.dateFrom.trim());
				} else {
					url.searchParams.delete('dateFrom');
				}
				
		if (filters.dateTo && typeof filters.dateTo === 'string' && filters.dateTo.trim()) {
					url.searchParams.set('dateTo', filters.dateTo.trim());
				} else {
					url.searchParams.delete('dateTo');
				}
				
				// Update category filters (reuse already fetched selectedCategories)
				if (selectedCategories && Object.keys(selectedCategories).length > 0) {
					// selectedCategories is object like {L: ['EN', 'RU'], F: ['AUDIO'], M: ['Bu'], S: []}
					// Flatten to array of direct values (no prefixes)
					const allValues = [];
					for (const [group, values] of Object.entries(selectedCategories)) {
						if (Array.isArray(values) && values.length > 0) {
							allValues.push(...values);
						}
					}
					
					if (allValues.length > 0) {
						// Use - instead of , to avoid URL encoding (%2C)
						url.searchParams.set('cat', allValues.join('-'));
					} else {
						url.searchParams.delete('cat');
					}
				} else {
					url.searchParams.delete('cat');
				}
				
				// Update public only filter (pu parameter)
				// Only for guests - authenticated users don't need this filter
				const isAuthenticated = !!(window.SERVER_AUTH_STATUS && window.SERVER_AUTH_STATUS.isAuthenticated);
				const publicOnlyCheckbox = document.getElementById('public-only-filter');
				if (!isAuthenticated && publicOnlyCheckbox) {
					const publicOnly = publicOnlyCheckbox.checked;
					if (publicOnly) {
						url.searchParams.set('pu', '1');
					} else {
						// Set pu=0 when disabled (don't remove parameter for guests)
						url.searchParams.set('pu', '0');
					}
				} else {
					// Remove pu parameter for authenticated users
					url.searchParams.delete('pu');
				}
				
				// Remove page parameter ONLY when filters changed (new search executed)
				// NEVER remove page when filters didn't change (pagination navigation)
				if (stateChangedFromLast) {
					// Filters changed - this is a NEW search, reset to page 1
					url.searchParams.delete('page');
					if (debugMode === '1') {
						console.log('[FilterController] NEW SEARCH: Filters changed, removing page parameter (reset to page 1)');
					}
				} else {
					// Filters didn't change - this is PAGINATION, preserve page parameter from URL
					// User clicked pagination link, page changed, but filters stayed the same
					if (currentPage) {
						// Remove page=1 to keep URL clean, but preserve other page numbers
						if (currentPage === '1') {
							url.searchParams.delete('page');
							if (debugMode === '1') {
							}
						} else {
							url.searchParams.set('page', currentPage);
							if (debugMode === '1') {
							}
						}
					} else if (debugMode === '1') {
					}
				}
				
				// Preserve hash (fulltext/files) unless explicitly changed
				// Don't modify hash here - it's managed separately
				
				// Compare new URL with current URL to avoid duplicate history entries
				const newUrlString = url.toString();
				const currentUrlString = window.location.href;
				
				// If URL is the same (excluding hash), don't create history entry
				const urlWithoutHash = new URL(newUrlString);
				const currentUrlWithoutHash = new URL(currentUrlString);
				urlWithoutHash.hash = '';
				currentUrlWithoutHash.hash = '';
				
				// CRITICAL: Check if URL actually changed before creating history entry
				// If current URL already matches new state, use replaceState to avoid duplicate entries
				const urlChanged = urlWithoutHash.toString() !== currentUrlWithoutHash.toString();
				
				if (debugMode === '1') {
					if (false) console.log('[FilterController] 🔍 URL comparison:', {
						currentUrlWithoutHash: currentUrlWithoutHash.toString(),
						newUrlWithoutHash: urlWithoutHash.toString(),
						urlChanged,
						currentUrlFull: currentUrlString,
						newUrlFull: newUrlString,
						currentHash: currentUrlWithoutHash.hash || '(none)',
						newHash: url.hash || '(none)'
					});
				}
				
				// CRITICAL: If URL didn't change, it means current URL already matches new state
				// This can happen when user returns to same search pattern
				// Use replaceState to avoid creating duplicate consecutive entries
				if (!urlChanged) {
					// Current URL already matches new state - use replaceState to avoid duplicate
					if (debugMode === '1') {
					}
					window.history.replaceState({}, '', url);
					// Update lastUrlState even if we didn't create a new history entry
					lastUrlState = currentState;
					if (window.__filterControllerUrlState) {
						window.__filterControllerUrlState.lastUrlState = currentState;
						if (typeof window.__filterControllerUrlState.setLastUrlState === 'function') {
							window.__filterControllerUrlState.setLastUrlState(currentState);
						}
					}
					isUpdatingUrl = false;
					return;
				}
				
				// URL changed - check if state also changed from last known state
				// This prevents duplicate entries when user returns to same pattern after other searches
				// But allows creating new entry when user navigates to different state
				if (!stateChangedFromLast && lastUrlState !== null) {
					// State didn't change from last known state, but URL changed
					// This might be pagination or hash change - use replaceState to avoid duplicate
					if (debugMode === '1') {
						console.log('[FilterController] ⚠️ State unchanged from last, but URL changed (pagination/hash?), using replaceState');
					}
					window.history.replaceState({}, '', url);
					// Update lastUrlState
					lastUrlState = currentState;
					if (window.__filterControllerUrlState) {
						window.__filterControllerUrlState.lastUrlState = currentState;
						if (typeof window.__filterControllerUrlState.setLastUrlState === 'function') {
							window.__filterControllerUrlState.setLastUrlState(currentState);
						}
					}
					isUpdatingUrl = false;
					return;
				}
				
				// Update lastUrlState BEFORE creating history entry to prevent duplicate calls
				// This ensures that if updateUrlWithFilters is called again with same state,
				// it will detect that state hasn't changed
				lastUrlState = currentState;
				if (window.__filterControllerUrlState) {
					window.__filterControllerUrlState.lastUrlState = currentState;
					if (typeof window.__filterControllerUrlState.setLastUrlState === 'function') {
						window.__filterControllerUrlState.setLastUrlState(currentState);
					}
				}
				
				// URL changed AND state changed - create new history entry
				// Use pushState for significant changes (creates history entry)
				if (usePushState) {
					window.history.pushState({}, '', url);
					if (debugMode === '1') {
						console.log('[FilterController] ➕ pushState (URL and state changed) - CREATING HISTORY ENTRY:', url.toString());
					}
				} else {
					// Use replaceState for debounced text updates (doesn't create history entry)
					window.history.replaceState({}, '', url);
					if (debugMode === '1') {
						console.log('[FilterController] 🔄 replaceState (no history entry):', url.toString());
					}
				}
				if (window.__filterControllerUrlState) {
					window.__filterControllerUrlState.lastUrlState = currentState;
					// Also update the internal state via setter if available
					if (typeof window.__filterControllerUrlState.setLastUrlState === 'function') {
						window.__filterControllerUrlState.setLastUrlState(currentState);
					}
				}
			} catch (error) {
				if (debugMode === '1') {
					console.error('[FilterController] Error updating URL:', error);
				}
			}
		};
		
		// URL should only be updated when search is executed (immediate=true)
		// Apply debounce for ALL URL updates to ensure URL changes only 1.5 seconds after user stops typing/changing filters
		if (immediate) {
			// Cancel any pending debounced update
			if (urlUpdateTimeout) {
				if (debugMode === '1') {
					console.log('[FilterController] ⏹️ Cancelling pending URL update timeout');
				}
				clearTimeout(urlUpdateTimeout);
				urlUpdateTimeout = null;
			}
			// Debounce URL update - wait 1.5 seconds after last change before updating URL
			if (debugMode === '1') {
				console.log('[FilterController] ⏰ Scheduling URL update debounce:', URL_UPDATE_DEBOUNCE_MS, 'ms');
			}
			// Store callback for flush capability
			urlUpdateCallback = () => {
				if (debugMode === '1') {
					console.log('[FilterController] ⏰ Debounce timeout fired, calling updateUrl');
				}
				updateUrl(true); // true = use pushState
				urlUpdateTimeout = null;
				urlUpdateCallback = null;
				// Reset flag after URL update completes
				setTimeout(() => {
					isUpdatingUrl = false;
					if (debugMode === '1') {
						console.log('[FilterController] ✅ URL update completed, flag reset');
					}
				}, 100);
			};
			urlUpdateTimeout = setTimeout(urlUpdateCallback, URL_UPDATE_DEBOUNCE_MS);
		} else {
			// This should never happen - URL updates only on search execution
			// But if called with immediate=false, do nothing (don't create debounced update)
			if (debugMode === '1') {
				console.warn('[FilterController] updateUrlWithFilters called with immediate=false - this should not happen');
			}
		}
		
	} catch (error) {
		if (debugMode === '1') { console.error('[FilterController] Error updating URL:', error); }
	} finally {
		// Reset flag after a short delay to allow URL update to complete
		setTimeout(() => {
			isUpdatingUrl = false;
		}, 100);
	}
}

/**
 * Flush URL debounce - immediately execute pending URL update
 * Used when user explicitly triggers search (Enter key, search button) to ensure latest filter changes are applied to URL
 * @param {Object} ctx - FilterController context
 */
export function flushUrlDebounce(ctx) {
	if (urlUpdateTimeout && urlUpdateCallback) {
		if (debugMode === '1') {
			console.log('[FilterController] 🚀 Flushing URL debounce - executing pending URL update immediately');
		}
		clearTimeout(urlUpdateTimeout);
		urlUpdateTimeout = null;
		// Execute callback immediately
		urlUpdateCallback();
		urlUpdateCallback = null;
	}
}



